import {Box, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {fetchWidgetSuccess} from 'redux/actions';
import {getEnv, isTrue} from './CommonUtil';

const IS_DEBUG = isTrue(getEnv('REACT_APP_DEBUG_MODE', 'false'));
const LOGS_ENABLED = isTrue(getEnv('REACT_APP_ENABLE_LOGS', 'false'));

const traceClick = (title, path) => {
  const time = new Date().toLocaleTimeString('en-US', {hour12: false});
  console.log(
    `[%c${time}%c] %c 🖱️ USER CLICKED: ${title} -> ${path} `,
    'color: blue; font-weight: normal;',
    'color: black; font-weight: bold;',
    'background: #FFD700; color: #000; font-weight: bold; padding: 2px 4px; border-radius: 2px;',
  );
};

const traceNavigation = (location) => {
  const time = new Date().toLocaleTimeString('en-US', {hour12: false});
  console.log(
    `[%c${time}%c] %c 🚩 [NAV]: ${location.pathname}${location.search}`,
    'color: #ccc;',
    'color: #fff;',
    'background: #000080; color: #fff; font-weight: bold; padding: 2px 4px;',
  );
};

/**
 * Redux Middleware to log actions only when enabled in .env
 */
export const loggerMiddleware = () => (next) => (action) => {
  if (LOGS_ENABLED) {
    const time = new Date().toLocaleTimeString('en-US', {hour12: false});
    console.groupCollapsed(
      `%c 🚀 ACTION: %c${action.type || 'ASYNC/THUNK'} %c@ ${time}`,
      'color: #0000ff; font-weight: bold;',
      'color: #fff; background: #222; padding: 2px 5px; border-radius: 3px;',
      'color: gray; font-weight: normal;',
    );
    console.log('Payload:', action.payload || 'No Payload');
    console.log('Full Action:', action);
    console.groupEnd();
  }
  return next(action);
};

/**
 * Globally listens for clicks on buttons and links
 */
export const initGlobalActivityTracker = (history) => {
  if (!LOGS_ENABLED) return;

  // USE 'true' AS THE THIRD ARGUMENT (Capture Phase)
  // This catches the click on the way DOWN before MUI stops it
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target.closest(
        'button, a, [role="button"], .MuiButton-root',
      );

      if (target) {
        const btnLabel =
          target.innerText?.trim().split('\n')[0] ||
          target.getAttribute('aria-label') ||
          'Action';

        const parentDialog = target.closest('.MuiDialog-root');
        let logMessage = btnLabel;
        if (parentDialog && parentDialog.id) {
          logMessage = `[${parentDialog.id}] ${btnLabel}`;
        }
        traceClick(logMessage, window.location.pathname);
      }
    },
    true,
  );

  if (history) {
    history.listen((location) => {
      traceNavigation(location);
    });
  }
};

/**
 * A reusable placeholder component for testing layout and props
 */
export const WidgetPlaceholder = ({widgetSpec, isSingleWidget, params}) => {
  if (!IS_DEBUG) return <></>;

  const dispatch = useDispatch();

  const {widgetid, widgettype} = widgetSpec;

  useEffect(() => {
    dispatch(fetchWidgetSuccess(widgetid, null));
  }, [dispatch, widgetid]);

  return (
    <Box
      sx={{
        p: 3,
        border: '1px dashed grey',
        textAlign: 'center',
        bgcolor: '#fafafa',
      }}
    >
      <Typography variant='h6' sx={{color: '#666'}}>
        Widget Placeholder
      </Typography>
      <Box sx={{mt: 1, textAlign: 'left', display: 'inline-block'}}>
        <Typography variant='body2'>
          <strong>ID:</strong> {widgetid}
        </Typography>
        <Typography variant='body2'>
          <strong>Type:</strong> {widgettype}
        </Typography>
        <Typography variant='body2'>
          <strong>Single Mode:</strong> {isSingleWidget ? '✅ Yes' : '❌ No'}
        </Typography>
        <Typography variant='body2'>
          <strong>Params:</strong> {JSON.stringify(params)}
        </Typography>
      </Box>
    </Box>
  );
};

export const debugLogApi = (type, config, dataOrError) => {
  if (!LOGS_ENABLED || !config) return config;

  const url = config?.url || 'Unknown URL';
  const method = config?.method?.toUpperCase() || 'METHOD';

  if (type === 'REQUEST') {
    console.groupCollapsed(`🚀 API REQUEST: ${method} ${url}`);
    console.info('Payload:', config.data);
    console.info('Headers:', config.headers);
    console.groupEnd();
  }

  if (type === 'RESPONSE') {
    console.groupCollapsed(`✅ API RESPONSE: ${method} ${url}`);
    console.info('Status:', dataOrError.status);
    console.info('Body:', dataOrError.data);
    console.groupEnd();
  }

  if (type === 'ERROR') {
    console.group(`❌ API ERROR: ${method} ${url}`);
    console.error(
      'Error Details:',
      dataOrError.response?.data || dataOrError.message,
    );
    console.groupEnd();
  }
  return config;
};

WidgetPlaceholder.propTypes = {
  widgetSpec: PropTypes.shape({
    widgetid: PropTypes.string,
    widgettype: PropTypes.string,
  }).isRequired,
  isSingleWidget: PropTypes.bool,
  params: PropTypes.object,
};
